import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCustomer } from '../../context/CustomerContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  RotateCcw, Plus, Package, Clock, AlertCircle, 
  CheckCircle, XCircle, ArrowLeft 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountReturns = () => {
  const { getAuthHeaders } = useCustomer();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [returns, setReturns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewReturn, setShowNewReturn] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [formData, setFormData] = useState({
    order_id: '',
    items: [],
    description: '',
    refund_method: 'original_payment'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReturns();
    fetchOrders();
    
    // Check if we should open new return form
    const orderId = searchParams.get('order');
    if (orderId) {
      setShowNewReturn(true);
      setFormData(prev => ({ ...prev, order_id: orderId }));
      checkEligibility(orderId);
    }
  }, []);

  const fetchReturns = async () => {
    try {
      const response = await axios.get(`${API}/customer/returns`, {
        headers: getAuthHeaders()
      });
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Error fetching returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/customer/orders?limit=50`, {
        headers: getAuthHeaders()
      });
      // Filter orders eligible for return
      const eligibleOrders = response.data.orders?.filter(o => 
        ['delivered', 'completed', 'shipped'].includes(o.order_status)
      ) || [];
      setOrders(eligibleOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const checkEligibility = async (orderId) => {
    try {
      const response = await axios.get(`${API}/customer/returns/eligibility/${orderId}`, {
        headers: getAuthHeaders()
      });
      setEligibility(response.data);
      if (response.data.eligible) {
        setSelectedOrder(orders.find(o => o.id === orderId));
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      setEligibility({ eligible: false, reasons: ['Unable to check eligibility'] });
    }
  };

  const handleOrderSelect = (orderId) => {
    setFormData(prev => ({ ...prev, order_id: orderId, items: [] }));
    setEligibility(null);
    checkEligibility(orderId);
    const order = orders.find(o => o.id === orderId);
    setSelectedOrder(order);
  };

  const handleItemToggle = (item, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          reason: 'changed_mind'
        }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter(i => i.product_id !== item.product_id)
      }));
    }
  };

  const handleItemReasonChange = (productId, reason) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(i => 
        i.product_id === productId ? { ...i, reason } : i
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.items.length === 0) {
      setError('Please select at least one item to return');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/returns`, formData, {
        headers: getAuthHeaders()
      });
      setShowNewReturn(false);
      fetchReturns();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      in_transit: 'bg-blue-100 text-blue-800',
      received: 'bg-purple-100 text-purple-800',
      refunded: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-returns">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif text-stone-800">Returns</h2>
        {!showNewReturn && (
          <Button 
            onClick={() => setShowNewReturn(true)}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="new-return-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Return
          </Button>
        )}
      </div>

      {showNewReturn ? (
        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setShowNewReturn(false);
                setFormData({ order_id: '', items: [], description: '', refund_method: 'original_payment' });
                setEligibility(null);
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="text-lg font-serif">Request a Return</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              {/* Order Selection */}
              <div className="space-y-2">
                <Label>Select Order</Label>
                <Select 
                  value={formData.order_id} 
                  onValueChange={handleOrderSelect}
                >
                  <SelectTrigger data-testid="order-select">
                    <SelectValue placeholder="Choose an order to return" />
                  </SelectTrigger>
                  <SelectContent>
                    {orders.map(order => (
                      <SelectItem key={order.id} value={order.id}>
                        {order.order_number} - {new Date(order.created_at).toLocaleDateString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Eligibility Check */}
              {eligibility && (
                <div className={`p-4 rounded-lg ${eligibility.eligible ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {eligibility.eligible ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-800">Eligible for return</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-5 w-5 text-red-600" />
                        <span className="font-medium text-red-800">Not eligible for return</span>
                      </>
                    )}
                  </div>
                  {!eligibility.eligible && eligibility.reasons?.length > 0 && (
                    <ul className="text-sm text-red-700 list-disc list-inside">
                      {eligibility.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  {eligibility.eligible && eligibility.days_remaining > 0 && (
                    <p className="text-sm text-green-700">
                      {eligibility.days_remaining} days remaining in return window
                    </p>
                  )}
                </div>
              )}

              {/* Item Selection */}
              {eligibility?.eligible && selectedOrder && (
                <>
                  <div className="space-y-4">
                    <Label>Select Items to Return</Label>
                    {selectedOrder.items?.map((item, idx) => {
                      const isSelected = formData.items.some(i => i.product_id === item.product_id);
                      return (
                        <div key={idx} className="p-4 bg-stone-50 rounded-lg space-y-3">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleItemToggle(item, checked)}
                              id={`item-${idx}`}
                            />
                            <div className="flex-1">
                              <label htmlFor={`item-${idx}`} className="font-medium text-stone-800 cursor-pointer">
                                {item.product_name}
                              </label>
                              <p className="text-sm text-stone-500">Qty: {item.quantity} • ${item.price}</p>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="pl-7">
                              <Label className="text-sm">Reason for return</Label>
                              <Select
                                value={formData.items.find(i => i.product_id === item.product_id)?.reason || 'changed_mind'}
                                onValueChange={(v) => handleItemReasonChange(item.product_id, v)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="defective">Defective/Damaged</SelectItem>
                                  <SelectItem value="wrong_item">Wrong Item Received</SelectItem>
                                  <SelectItem value="not_as_described">Not as Described</SelectItem>
                                  <SelectItem value="changed_mind">Changed My Mind</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Additional Details</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Please describe the issue or reason for return..."
                      rows={3}
                      data-testid="return-description"
                    />
                  </div>

                  {/* Refund Method */}
                  <div className="space-y-2">
                    <Label>Refund Method</Label>
                    <Select
                      value={formData.refund_method}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, refund_method: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="original_payment">Original Payment Method</SelectItem>
                        <SelectItem value="store_credit">Store Credit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-700 hover:bg-amber-800"
                    disabled={submitting || formData.items.length === 0}
                    data-testid="submit-return-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Return Request'}
                  </Button>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      ) : (
        <>
          {returns.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <RotateCcw className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-lg font-medium text-stone-800 mb-2">No returns</h3>
                <p className="text-stone-500 mb-4">
                  You haven't requested any returns yet
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {returns.map((ret) => (
                <Card key={ret.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="font-semibold text-stone-800">{ret.rma_number}</p>
                        <p className="text-sm text-stone-500">
                          Order: {ret.order_number}
                        </p>
                        <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                          <Clock className="h-4 w-4" />
                          {new Date(ret.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={getStatusColor(ret.status)}>
                        {ret.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <div className="bg-stone-50 rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-stone-700 mb-2">Items:</p>
                      {ret.items?.map((item, idx) => (
                        <p key={idx} className="text-sm text-stone-600">
                          • {item.product_name} (x{item.quantity})
                        </p>
                      ))}
                    </div>

                    {ret.estimated_refund && (
                      <p className="text-sm text-stone-600">
                        Estimated refund: <span className="font-semibold">${ret.estimated_refund.toFixed(2)}</span>
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccountReturns;
